import { makeArticle } from "./learnFactory";
import type { LearnFAQ } from "./learnShared";
import type { RelatedLink as RL } from "@/components/RelatedPages";

const successRateRelated: RL[] = [
  {
    title: "SAINT® vs fMRI-Guided Accelerated TMS",
    href: "/learn/saint-vs-accelerated-tms",
    description:
      "What's the same, what isn't, and what the FDA clearance and trial data cover",
  },
  {
    title: "How Long Do SAINT® Results Last?",
    href: "/learn/what-happens-after-saint-tms",
    description: "Durability, follow-up data, and maintenance after SAINT®",
  },
  {
    title: "How Fast Does SAINT® TMS Work?",
    href: "/learn/how-fast-does-saint-tms-work",
    description: "Timeline for response during and after the 5-day protocol",
  },
  {
    title: "What If SAINT® Doesn't Work?",
    href: "/learn/what-if-saint-doesnt-work",
    description:
      "Next steps and options if SAINT® doesn't produce enough relief",
  },
  {
    title: "SAINT® Accelerated TMS",
    href: "/saint",
    description: "The 5-day, fMRI-guided protocol offered at our clinic",
  },
  {
    title: "What Is Treatment-Resistant Depression?",
    href: "/learn/what-is-treatment-resistant-depression",
    description: "Definition, causes, and treatment options",
  },
];

const faqs: LearnFAQ[] = [
  {
    question: "What is the success rate of SAINT®?",
    answer:
      "In the 2022 randomized controlled trial, 79% of participants reached remission after the five-day SAINT® protocol (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. Earlier open-label studies reported similar or higher remission rates, but open-label designs do not control for placebo effects the way a randomized trial does. The 79% figure is the most rigorous published result for the SAINT® system specifically.",
  },
  {
    question: "What does remission mean in the SAINT® studies?",
    answer:
      "Remission means a participant's depression score dropped below a set threshold on a standardized mood scale — in the SAINT® trials, the Montgomery-Åsberg Depression Rating Scale (MADRS). A MADRS score of 10 or below generally indicates remission. Remission is different from response, which means a meaningful improvement that doesn't necessarily reach the remission threshold. Remission is the stricter, more complete measure.",
  },
  {
    question: "How does SAINT® compare to regular TMS?",
    answer:
      "Standard TMS typically delivers one session per day over 6 weeks. Published remission rates for standard TMS range from about 30% to 40% in various studies. SAINT® compresses treatment into 5 days with 10 sessions per day and uses fMRI-guided personalized targeting. In the 2022 randomized trial, 79% of participants reached remission (Cole et al., 2022). Individual results may vary. The protocols differ in schedule, targeting, and the evidence behind each system.",
  },
  {
    question: "Are the SAINT® success rates from a real clinical trial?",
    answer:
      "Yes. The key result — 79% remission — comes from a double-blind randomized controlled trial published in the American Journal of Psychiatry in 2022 (Cole et al., 2022;179(2):132–141). The study compared SAINT® to a sham (placebo) control. Earlier open-label studies from the same Stanford research group reported similar rates, but the 2022 trial is the strongest published evidence because it was randomized and sham-controlled.",
  },
  {
    question: "Do the SAINT® results apply to other accelerated TMS?",
    answer:
      "No. The 79% remission figure comes from trials of the SAINT® system specifically — the FDA-cleared SAINT® Neuromodulation System by Magnus Medical, which uses its own fMRI-guided targeting software. Other accelerated TMS protocols may copy the schedule (10 sessions per day, 5 days, iTBS pulse pattern) but do not use the same FDA-cleared targeting system. The SAINT® trial outcomes do not transfer to those protocols. Ask any clinic for its own published outcomes.",
  },
  {
    question: "What happens in the brain before and after TMS?",
    answer:
      "Functional MRI (fMRI) scans in the SAINT® studies showed changes in brain connectivity after treatment. The targeting approach is based on an anticorrelation between two brain regions — the subgenual anterior cingulate cortex (sgACC) and the dorsolateral prefrontal cortex (DLPFC). After SAINT®, researchers observed normalization in the connectivity patterns associated with depression. These imaging findings are from the SAINT® research group and may not apply to other TMS protocols.",
  },
];

export const pageSaintTmsSuccessRates = makeArticle({
  slug: "saint-tms-success-rates",
  title: "SAINT® TMS Success Rates: What the Studies Show (2026)",
  description:
    "79% remission in the 2022 randomized trial (Cole et al., 4-week timepoint). Learn what the data show, why results vary, and why SAINT® outcomes don't transfer to off-label 5-day TMS.",
  canonical: "/learn/saint-tms-success-rates",
  h1: "SAINT® TMS Success Rates: What the Research Shows",
  breadcrumbLabel: "SAINT® TMS Success Rates",
  shortAnswer:
    "In the 2022 randomized controlled trial, 79% of participants reached remission after the five-day SAINT® protocol (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary. That figure comes from trials of the SAINT® system specifically — the FDA-cleared SAINT® Neuromodulation System by Magnus Medical — and does not transfer to other accelerated TMS protocols that copy the schedule but not the targeting system. If you are in crisis, call or text 988.",
  keyTakeaways: [
    "79% of participants in the 2022 randomized trial reached remission after five days (Cole et al., 2022). Individual results may vary.",
    "Remission means a depression score below a set threshold on a standardized scale — not just feeling better.",
    "Open-label studies reported similar rates, but the randomized trial is the strongest evidence.",
    "Standard TMS remission rates range from about 30–40% in published studies; SAINT® differs in schedule, targeting, and evidence.",
    "The 79% figure applies to the SAINT® system only, not to other accelerated TMS protocols.",
    "If you are in crisis, call or text 988.",
  ],
  sections: [
    {
      heading: "SAINT® TMS Success Rates: Quick Answer",
      paragraphs: [
        "SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is FDA-cleared for major depressive disorder (MDD) in adults whose depression has not improved enough on prior antidepressant medication. The most cited success rate comes from the 2022 double-blind randomized controlled trial: 79% of participants reached remission after the five-day protocol (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary.",
        "That 79% remission figure is specific to the SAINT® system — the FDA-cleared SAINT® Neuromodulation System by Magnus Medical, which uses proprietary fMRI-guided targeting software. Other accelerated TMS protocols may copy the treatment schedule but do not use the same targeting system, and the SAINT® trial outcomes do not transfer to them. NWCOA is a Verified SAINT® Provider in Coral Springs, Florida. [[Learn more about SAINT®|/saint]] or [[check if you're a candidate|/am-i-a-candidate-screener]].",
      ],
    },
    {
      heading: "Open-Label vs Randomized Results",
      paragraphs: [
        "The SAINT® research program at Stanford produced several studies before the pivotal 2022 trial. The earlier studies were open-label, meaning participants and researchers knew they were receiving the real treatment. Open-label studies reported remission rates of roughly 78–80% or higher. These results were encouraging and justified a larger, more rigorous trial.",
        "The 2022 study was a double-blind randomized controlled trial — the gold standard in clinical research. Participants were randomly assigned to either real SAINT® or a sham (placebo) control, and neither they nor the raters knew which group they were in. In that trial, 79% of participants who received SAINT® reached remission, compared to 13% in the sham group (Cole et al., Am J Psychiatry 2022;179(2):132–141). Individual results may vary.",
        "The randomized trial is the strongest published evidence because it controls for placebo effects and researcher bias. When someone asks about the SAINT® success rate, the 79% figure from the 2022 randomized trial is the most reliable answer.",
      ],
    },
    {
      heading: "Response vs Remission: What Each Means",
      paragraphs: [
        "Two terms come up in depression treatment research: response and remission. They measure different levels of improvement, and the distinction matters.",
        "Response means a meaningful improvement in depression symptoms — typically a 50% or greater drop in score on a standardized mood scale. A responder feels better, but may still have significant symptoms. In the SAINT® studies, response rates were even higher than remission rates.",
        "Remission is stricter. It means the depression score drops below a set threshold — in the SAINT® trials, a score of 10 or below on the Montgomery-Åsberg Depression Rating Scale (MADRS). Remission suggests the depression is no longer meeting the clinical threshold for a major depressive episode. The 79% figure from the 2022 trial refers to remission, not just response. Individual results may vary.",
      ],
    },
    {
      heading: "SAINT® Success Rates vs Standard TMS and Antidepressants",
      paragraphs: [
        "To put the 79% remission rate in context, it helps to compare it to other depression treatments. Standard TMS — one session per day over about 6 weeks — has published remission rates ranging from about 30% to 40% across various studies. Antidepressant medication has lower rates, especially for treatment-resistant depression.",
        "The STAR*D study, a large federally funded trial of antidepressant medications, found that after one antidepressant, about 28% of participants achieved remission. After two medications, the rate dropped further. By the time patients had tried multiple medications without success — the population SAINT® is designed for — the odds of remission from the next medication alone were quite low.",
        "SAINT® is designed for adults whose depression has not improved enough on prior antidepressant medication. The 79% remission rate in the 2022 trial was achieved in that treatment-resistant population (Cole et al., 2022). Individual results may vary. The comparison is not about one treatment being universally better — it is about which evidence applies to which system and which patient group.",
      ],
    },
    {
      heading: "SAINT® Success Rates Compared to Other Treatments",
      table: {
        headers: ["Treatment", "Response rate", "Remission rate", "Source"],
        rows: [
          [
            "SAINT® (2022 randomized trial)",
            "Not separately reported; remission was the primary endpoint",
            "79%",
            "Cole et al., Am J Psychiatry 2022;179(2):132–141",
          ],
          [
            "SAINT® (sham control group)",
            "—",
            "13%",
            "Cole et al., Am J Psychiatry 2022;179(2):132–141",
          ],
          [
            "Standard TMS (various studies)",
            "Varies; typically 50–60%",
            "Approximately 30–40%",
            "Published TMS literature; rates vary by study and population",
          ],
          [
            "First antidepressant (STAR*D level 1)",
            "47%",
            "28%",
            "Trivedi et al., Am J Psychiatry 2006;163(11):1905–1917",
          ],
          [
            "Second antidepressant (STAR*D level 2)",
            "31%",
            "18%",
            "Rush et al., Am J Psychiatry 2006;163(11):1905–1917",
          ],
        ],
      },
    },
    {
      heading: "What Changes in the Brain: Before and After TMS",
      paragraphs: [
        "The SAINT® targeting approach is based on brain imaging. Researchers use functional MRI (fMRI) to identify a personalized target for stimulation in each person. The target is chosen based on an anticorrelation between two brain regions: the subgenual anterior cingulate cortex (sgACC), which is often overactive in depression, and the dorsolateral prefrontal cortex (DLPFC), the area stimulated during TMS.",
        "Anticorrelation means that when one region is more active, the other tends to be less active. In depression, this normal push-and-pull between the two regions can be disrupted. SAINT® aims to stimulate the specific DLPFC location that has the strongest anticorrelation with the sgACC for each individual — the spot where stimulation is most likely to calm the overactive region.",
        "After SAINT®, the Stanford research group reported changes in brain connectivity visible on fMRI — patterns associated with depression appeared to normalize. These imaging findings are from the SAINT® research program and apply to the SAINT® targeting system. They do not necessarily transfer to other TMS protocols that use different targeting methods. [[Learn about fMRI-guided TMS|/learn/fmri-guided-tms-for-depression]].",
      ],
    },
    {
      heading: "Patient Stories",
      paragraphs: [
        "We share patient stories only with signed consent and HIPAA authorization, and no story is fabricated. Individual results may vary; no outcome is guaranteed. Until consented stories are available, the trial data above is the strongest published evidence for SAINT®, and you can read verified Google reviews for our Coral Springs clinic on our contact page.",
      ],
    },
    {
      heading: "Do These Results Apply to You?",
      paragraphs: [
        "The 79% remission rate comes from a specific trial of a specific system. It applies to the SAINT® Neuromodulation System by Magnus Medical, used in adults with major depressive disorder whose depression had not improved enough on prior antidepressant medication. NWCOA treats adults 22 and older.",
        "SAINT® is FDA-cleared for MDD in adults. It is not cleared for anxiety, OCD, PTSD, bipolar disorder, or any other condition. If you have treatment-resistant depression and want to know whether SAINT® may be appropriate for you, the first step is a free consultation. [[Check if you're a candidate|/am-i-a-candidate-screener]] or [[verify your insurance benefits|/check-insurance-options]]. SAINT® at NWCOA starts at $25,000; coverage varies by plan and we verify your benefits for free.",
      ],
    },
  ],
  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://pubmed.ncbi.nlm.nih.gov/35624717/",
    },
    {
      citation:
        "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2020;177(8):716-726.",
      url: "https://pubmed.ncbi.nlm.nih.gov/32463539/",
    },
    {
      citation:
        "Cole EJ, Phillips AL, Bentzley BS, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for treatment-resistant depression. Am J Psychiatry. 2021;178(10):898-909.",
      url: "https://pubmed.ncbi.nlm.nih.gov/34256788/",
    },
    {
      citation:
        "Trivedi MH, et al. Evaluation of outcomes with citalopram for depression using STAR*D. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074942/",
    },
    {
      citation:
        "Rush AJ, et al. Acute and longer-term outcomes in STAR*D. Am J Psychiatry. 2006;163(11):1905-1917.",
      url: "https://pubmed.ncbi.nlm.nih.gov/17074943/",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],
  relatedLinks: successRateRelated,
  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    reviewedByHref: "/dr-daniel-pietras-md",
    published: "October 2026",
    lastReviewed: "October 2026",
    lastUpdated: "October 2026",
  },
  toc: [
    {
      label: "Quick Answer",
      href: "#saint-tms-success-rates-quick-answer",
    },
    {
      label: "Open-Label vs Randomized",
      href: "#open-label-vs-randomized-results",
    },
    {
      label: "Response vs Remission",
      href: "#response-vs-remission-what-each-means",
    },
    {
      label: "vs Standard TMS & Antidepressants",
      href: "#saint-success-rates-vs-standard-tms-and-antidepressants",
    },
    {
      label: "Comparison Table",
      href: "#saint-success-rates-compared-to-other-treatments",
    },
    {
      label: "Brain Before & After TMS",
      href: "#what-changes-in-the-brain-before-and-after-tms",
    },
    {
      label: "Patient Story",
      href: "#a-patient-story",
    },
    {
      label: "Do These Results Apply to You?",
      href: "#do-these-results-apply-to-you",
    },
    { label: "FAQ", href: "#faq" },
    { label: "Cited Research", href: "#cited-research" },
  ],
  safetyBlock:
    "SAINT® is not for everyone. It is contraindicated for people with non-removable magnetic-sensitive metal in or near the head, certain implanted devices, or increased intracranial pressure. Possible side effects include headache, fatigue, scalp discomfort and anxiety; TMS carries a small risk of seizure and, as with any depression treatment, a risk of worsening depression or suicidal thoughts. If you are in crisis, call or text 988.",
  trademarkFooter:
    "SAINT® is a registered trademark of Stanford University, exclusively licensed to Magnus Medical, Inc. NWCOA is not affiliated with Stanford University.",
  ctaBand: {
    headline: "See if SAINT® is a fit, free",
    primaryText: "Learn If It's a Fit",
    primaryHref: "/am-i-a-candidate-screener",
    secondaryText: "Check my insurance",
    secondaryHref: "/check-insurance-options",
    phone: "954-827-9793",
  },
});
